import { useState, useCallback, FormEvent, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, LogIn, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import config from '@/config';

function formatLockoutTime(seconds: number): string {
  if (seconds <= 0) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

export function LoginPage() {
  const { login, isLoading: authLoading, lockout, checkLockout } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkLockout();
  }, [checkLockout]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        await login(username, password);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed';
        setError(message);
        try {
          await checkLockout();
        } catch {
          // best-effort
        }
      } finally {
        setSubmitting(false);
      }
    },
    [login, username, password, checkLockout]
  );

  const isLocked = lockout?.locked ?? false;
  const canSubmit = !submitting && !isLocked && username.length > 0 && password.length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">{config.appName}</CardTitle>
          <CardDescription>Sign in to access the admin panel</CardDescription>
        </CardHeader>
        <CardContent>
          {authLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading…</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {isLocked && lockout && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Too many failed attempts. Try again in{' '}
                    <strong>{formatLockoutTime(lockout.remaining_seconds)}</strong>.
                  </AlertDescription>
                </Alert>
              )}

              {error && !isLocked && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLocked || submitting}
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLocked || submitting}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={!canSubmit}>
                <LogIn className="h-4 w-4 mr-2" />
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>

              <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3 w-3" />
                <span>Brute-force protection is active</span>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginPage;
